import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../../services/api.ts";
import { getMediaUrl } from "../../services/media.ts";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Navbar from "../navbar/Navbar";
import PatientSelector from "./PatientSelector.jsx";
import EHRViewer from "./EHRViewer.jsx";
import DatasetFormModal from "./DatasetFormModal";
import AssignProjectModal from "./AssignProjectModal";
import { toggleDatasetPublic, removeDatasetFromMyDatasets, updateDownloadPermission } from "../../services/datasets";
import "./Datasets.css";
import "../shared/starfield.css";

const DatasetList = () => {
    const params = useParams();
    const navigate = useNavigate();
    const datasetId = params.DatasetId;
    const [dataset, setDataset] = useState(null);
    const [dataByPatient, setDataByPatient] = useState({});
    const [expandedPatients, setExpandedPatients] = useState({});
    const [expandedModalities, setExpandedModalities] = useState({});
    const [activeTab, setActiveTab] = useState("all");
    const [loading, setLoading] = useState(true);
    const [showAddPatientsModal, setShowAddPatientsModal] = useState(false);
    const [selectedPatients, setSelectedPatients] = useState([]);
    const [addingPatients, setAddingPatients] = useState(false);

    // EHR Viewer state
    const [showEHRViewer, setShowEHRViewer] = useState(false);
    const [selectedEHRData, setSelectedEHRData] = useState(null);

    // Dataset actions (moved from /Datasets list page)
    const [projects, setProjects] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ show: false, deletePatients: false });

    const fetchDataset = useCallback(async () => {
        try {
            setLoading(true);
            const response = await API.get(`/datasets/${datasetId}/`);
            setDataset(response.data);
            setDataByPatient(response.data.data_by_patient || {});

            // All patients collapsed by default
            const expanded = {};
            const modalityExpanded = {};
            Object.keys(response.data.data_by_patient || {}).forEach(patientId => {
                expanded[patientId] = false; // Collapsed by default
                modalityExpanded[patientId] = {
                    imaging: true,
                    images: true,
                    documents: true,
                    tables: true,
                    signals: true,
                    ehr: true,
                    annotations: false, // Annotations collapsed by default
                    maps: false, // Maps collapsed by default
                };
            });
            setExpandedPatients(expanded);
            setExpandedModalities(modalityExpanded);
        } catch (err) {
            console.error("Error fetching dataset:", err);
            toast.error("Failed to fetch dataset.");
        } finally {
            setLoading(false);
        }
    }, [datasetId]);

    useEffect(() => {
        fetchDataset();
    }, [fetchDataset]);

    // Projects are needed for the "Assign to Project" modal.
    const fetchProjects = useCallback(async () => {
        try {
            const response = await API.get("/projects/");
            setProjects(response.data);
        } catch (err) {
            console.error("Error fetching projects:", err);
        }
    }, []);

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    const handleTogglePublic = async () => {
        if (!dataset) return;
        try {
            const result = await toggleDatasetPublic(dataset.id);
            toast.success(result.message);
            fetchDataset();
        } catch (err) {
            console.error("Error toggling public status:", err);
            toast.error(err.response?.data?.error || "Failed to update public status");
        }
    };

    const handleToggleDownload = async () => {
        if (!dataset) return;
        try {
            const result = await updateDownloadPermission(dataset.id, !dataset.allow_download);
            toast.success(result.message);
            fetchDataset();
        } catch (err) {
            console.error("Error toggling download permission:", err);
            toast.error(err.response?.data?.error || "Failed to update download permission");
        }
    };

    const handleRemoveFromMyDatasets = async () => {
        if (!dataset) return;
        if (!window.confirm(`Remove "${dataset.name}" from your datasets? You won't be able to access it anymore.`)) return;
        try {
            const result = await removeDatasetFromMyDatasets(dataset.id);
            toast.success(result.message);
            navigate("/Datasets");
        } catch (err) {
            console.error("Error removing dataset:", err);
            toast.error(err.response?.data?.error || "Failed to remove dataset");
        }
    };

    const confirmDeleteDataset = async () => {
        if (!dataset) return;
        const { deletePatients } = deleteModal;
        setDeleteModal({ show: false, deletePatients: false });
        try {
            await API.request({
                method: 'DELETE',
                url: `/datasets/${dataset.id}/`,
                data: { delete_patients: deletePatients }
            });
            toast.success(
                deletePatients
                    ? "Dataset and all its patients deleted successfully."
                    : "Dataset deleted successfully."
            );
            navigate("/Datasets");
        } catch (err) {
            console.error("Error deleting dataset:", err);
            toast.error(err.response?.data?.error || "Failed to delete dataset.");
        }
    };

    const togglePatient = (patientId) => {
        setExpandedPatients(prev => ({
            ...prev,
            [patientId]: !prev[patientId]
        }));
    };

    const toggleModality = (patientId, modality) => {
        setExpandedModalities(prev => ({
            ...prev,
            [patientId]: {
                ...prev[patientId],
                [modality]: !prev[patientId]?.[modality]
            }
        }));
    };

    const expandAllPatients = () => {
        const expanded = {};
        Object.keys(dataByPatient).forEach(patientId => {
            expanded[patientId] = true;
        });
        setExpandedPatients(expanded);
    };

    const collapseAllPatients = () => {
        const expanded = {};
        Object.keys(dataByPatient).forEach(patientId => {
            expanded[patientId] = false;
        });
        setExpandedPatients(expanded);
    };

    // Modality icons and colors
    const modalityConfig = {
        imaging: { icon: 'fa-x-ray', color: 'primary', label: 'Medical Imaging' },
        images: { icon: 'fa-image', color: 'success', label: 'Images' },
        documents: { icon: 'fa-file-text', color: 'warning', label: 'Documents' },
        tables: { icon: 'fa-table', color: 'info', label: 'Tables' },
        signals: { icon: 'fa-wave-square', color: 'secondary', label: 'Signals' },
        ehr: { icon: 'fa-heartbeat', color: 'danger', label: 'EHR' },
        annotations: { icon: 'fa-pencil-square', color: 'purple', label: 'Annotations' },
        maps: { icon: 'fa-map', color: 'teal', label: 'Maps' },
    };

    const getFileTypeIcon = (fileType) => {
        const icons = {
            nifti: 'fa-cube',
            dicom: 'fa-x-ray',
            numpy: 'fa-code',
            jpg: 'fa-image',
            jpeg: 'fa-image',
            png: 'fa-image',
            pdf: 'fa-file-pdf-o',
            doc: 'fa-file-word-o',
            docx: 'fa-file-word-o',
            txt: 'fa-file-text-o',
            csv: 'fa-file-excel-o',
            xlsx: 'fa-file-excel-o',
            xls: 'fa-file-excel-o',
            edf: 'fa-signal',
            json: 'fa-code',
        };
        return icons[fileType] || 'fa-file';
    };

    const handleOpenEHR = async (ehrItem) => {
        try {
            // If we have the file path, try to fetch the JSON content
            if (ehrItem.file || ehrItem.location) {
                const fileUrl = getMediaUrl(ehrItem.file || ehrItem.location);
                const response = await fetch(fileUrl);
                if (response.ok) {
                    const content = await response.json();
                    setSelectedEHRData({
                        ...ehrItem,
                        content: content
                    });
                    setShowEHRViewer(true);
                } else {
                    // If fetch fails, show with metadata only
                    setSelectedEHRData({
                        ...ehrItem,
                        content: ehrItem.metadata || {}
                    });
                    setShowEHRViewer(true);
                }
            } else {
                setSelectedEHRData({
                    ...ehrItem,
                    content: ehrItem.metadata || {}
                });
                setShowEHRViewer(true);
            }
        } catch (err) {
            console.error("Error loading EHR data:", err);
            // Fallback to showing metadata
            setSelectedEHRData({
                ...ehrItem,
                content: ehrItem.metadata || { error: "Could not load EHR data" }
            });
            setShowEHRViewer(true);
        }
    };

    const handleDownloadFile = (item) => {
        try {
            const fileUrl = getMediaUrl(item.file || item.location);
            window.open(fileUrl, '_blank');
        } catch (err) {
            console.error("Error downloading file:", err);
            toast.error("Failed to download file.");
        }
    };

    // Calculate totals
    const getTotals = () => {
        let totals = {
            patients: Object.keys(dataByPatient).length,
            imaging: 0,
            images: 0,
            documents: 0,
            tables: 0,
            signals: 0,
            ehr: 0,
            annotations: 0,
            maps: 0,
            total: 0,
        };

        Object.values(dataByPatient).forEach(patientData => {
            if (patientData.counts) {
                totals.imaging += patientData.counts.imaging || 0;
                totals.images += patientData.counts.images || 0;
                totals.documents += patientData.counts.documents || 0;
                totals.tables += patientData.counts.tables || 0;
                totals.signals += patientData.counts.signals || 0;
                totals.ehr += patientData.counts.ehr || 0;
                totals.annotations += patientData.counts.annotations || 0;
                totals.maps += patientData.counts.maps || 0;
                totals.total += patientData.counts.total || 0;
            }
        });

        return totals;
    };

    const totals = getTotals();

    // Filter data based on active tab
    const getFilteredData = () => {
        if (activeTab === "all") return dataByPatient;

        const filtered = {};
        Object.entries(dataByPatient).forEach(([patientId, data]) => {
            const counts = data.counts || {};
            if (activeTab === "imaging" && counts.imaging > 0) {
                filtered[patientId] = data;
            } else if (activeTab === "ehr" && counts.ehr > 0) {
                filtered[patientId] = data;
            } else if (activeTab === "annotations" && counts.annotations > 0) {
                filtered[patientId] = data;
            } else if (activeTab === "documents" && counts.documents > 0) {
                filtered[patientId] = data;
            } else if (activeTab === "signals" && counts.signals > 0) {
                filtered[patientId] = data;
            } else if (activeTab === "tables" && counts.tables > 0) {
                filtered[patientId] = data;
            } else if (activeTab === "maps" && counts.maps > 0) {
                filtered[patientId] = data;
            }
        });
        return filtered;
    };

    const handleAddPatients = async () => {
        if (selectedPatients.length === 0) {
            toast.warning("Please select at least one patient to add.");
            return;
        }

        setAddingPatients(true);
        try {
            const patientIds = selectedPatients.map(p => p.id);
            await API.post(`/datasets/${datasetId}/add-patients/`, {
                patient_ids: patientIds
            });
            toast.success(`Added ${selectedPatients.length} patient(s) to the dataset.`);
            setShowAddPatientsModal(false);
            setSelectedPatients([]);
            fetchDataset();
        } catch (err) {
            console.error("Error adding patients:", err);
            toast.error("Failed to add patients to dataset.");
        } finally {
            setAddingPatients(false);
        }
    };

    const renderDataItem = (item, modality) => {
        const isEHR = modality === 'ehr';
        const config = modalityConfig[modality] || modalityConfig.documents;

        return (
            <div key={item.id} className="data-item">
                <div className="data-item-icon">
                    <i className={`fa ${getFileTypeIcon(item.original_file_type)}`}></i>
                </div>
                <div className="data-item-info">
                    <span className="data-item-name">{item.filename || 'Data Item'}</span>
                    <span className="data-item-meta">
                        <span className={`badge bg-${config.color} me-1`}>
                            {modality.toUpperCase()}
                        </span>
                        {item.original_file_type && (
                            <span className="badge bg-dark me-1">
                                {item.original_file_type.toUpperCase()}
                            </span>
                        )}
                        {item.description && <small className="text-muted">{item.description}</small>}
                    </span>
                </div>
                <div className="data-item-actions">
                    {isEHR && item.original_file_type === 'json' && (
                        <button
                            className="btn btn-sm btn-outline-info me-1"
                            onClick={() => handleOpenEHR(item)}
                            title="View EHR"
                        >
                            <i className="fa fa-eye"></i>
                        </button>
                    )}
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleDownloadFile(item)}
                        title="Download"
                    >
                        <i className="fa fa-download"></i>
                    </button>
                </div>
            </div>
        );
    };

    // State for expanded studies and series
    const [expandedStudies, setExpandedStudies] = useState({});
    const [expandedSeries, setExpandedSeries] = useState({});
    // Series being renamed: { key: `${patientId}-${studyId}-${seriesId}`, value: draft string }
    // `null` when no rename is in progress.
    const [renamingSeries, setRenamingSeries] = useState(null);
    const [renameSubmitting, setRenameSubmitting] = useState(false);

    const toggleStudy = (patientId, studyId) => {
        const key = `${patientId}-${studyId}`;
        setExpandedStudies(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const toggleSeries = (patientId, studyId, seriesId) => {
        const key = `${patientId}-${studyId}-${seriesId}`;
        setExpandedSeries(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const startRenameSeries = (patientId, studyId, seriesId, currentName) => {
        setRenamingSeries({
            key: `${patientId}-${studyId}-${seriesId}`,
            patientId,
            studyId,
            oldSeriesId: seriesId,
            draft: currentName ?? seriesId,
        });
    };

    const cancelRenameSeries = () => {
        setRenamingSeries(null);
    };

    const submitRenameSeries = async () => {
        if (!renamingSeries) return;
        const { patientId, studyId, oldSeriesId, draft } = renamingSeries;
        const newName = (draft || '').trim();
        if (!newName || newName === oldSeriesId) {
            setRenamingSeries(null);
            return;
        }
        try {
            setRenameSubmitting(true);
            const resp = await API.post(`/datasets/${datasetId}/rename-series/`, {
                patient_id: patientId,
                study_id: studyId,
                old_series_id: oldSeriesId,
                new_name: newName,
            });
            const updated = resp?.data?.updated ?? 0;
            if (updated === 0) {
                toast.warning('No series rows matched — nothing renamed.');
            } else {
                toast.success(`Renamed ${updated} row${updated === 1 ? '' : 's'} to "${newName}".`);
            }
            setRenamingSeries(null);
            // Reload the dataset so the imaging hierarchy re-groups under the
            // new series key. Local mutation isn't enough because the series
            // key changes and other rows may collapse under the same name.
            fetchDataset();
        } catch (err) {
            const detail = err?.response?.data?.error || err?.response?.data?.detail || err.message;
            toast.error(`Rename failed: ${detail}`);
        } finally {
            setRenameSubmitting(false);
        }
    };

    // Render the imaging hierarchy (Study > Series) for DICOM/NIfTI
    const renderImagingHierarchy = (patientId, patientData) => {
        const hierarchy = patientData.imaging_hierarchy;
        if (!hierarchy || !hierarchy.studies || hierarchy.studies.length === 0) {
            // Fall back to flat list if no hierarchy
            return renderModalitySection(patientId, 'imaging', patientData.modalities?.imaging);
        }

        if (activeTab !== "all" && activeTab !== "imaging") return null;

        const config = modalityConfig['imaging'];
        const isExpanded = expandedModalities[patientId]?.['imaging'] ?? true;
        const counts = patientData.counts || {};

        return (
            <div key="imaging" className="modality-section modality-imaging">
                <div
                    className="modality-header"
                    onClick={() => toggleModality(patientId, 'imaging')}
                    style={{ cursor: 'pointer' }}
                >
                    <div className="modality-title">
                        <i className={`fa ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`}></i>
                        <i className={`fa ${config.icon} me-2`}></i>
                        <span className={`badge bg-${config.color} me-2`}>{config.label}</span>
                        <span className="modality-count">({counts.imaging || 0})</span>
                    </div>
                </div>
                {isExpanded && (
                    <div className="modality-content">
                        {hierarchy.studies.map(study => {
                            const studyKey = `${patientId}-${study.study_id}`;
                            const isStudyExpanded = expandedStudies[studyKey] ?? true;

                            return (
                                <div key={study.study_id} className="study-section" style={{ marginBottom: '0.5rem' }}>
                                    <div
                                        className="study-header"
                                        onClick={() => toggleStudy(patientId, study.study_id)}
                                        style={{
                                            cursor: 'pointer',
                                            padding: '0.5rem 1rem',
                                            backgroundColor: '#f0f4ff',
                                            borderRadius: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginBottom: isStudyExpanded ? '0.5rem' : '0'
                                        }}
                                    >
                                        <i className={`fa ${isStudyExpanded ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`}></i>
                                        <i className="fa fa-folder-open me-2" style={{ color: '#3b82f6' }}></i>
                                        <span style={{ fontWeight: '500' }}>Study: {study.study_id}</span>
                                        <span className="badge bg-secondary ms-2">{study.series?.length || 0} series</span>
                                        <span className="badge bg-light text-dark ms-1">{study.file_count} files</span>
                                    </div>

                                    {isStudyExpanded && study.series && (
                                        <div className="series-list" style={{ paddingLeft: '1.5rem' }}>
                                            {study.series.map(series => {
                                                const seriesKey = `${patientId}-${study.study_id}-${series.series_id}`;
                                                const isSeriesExpanded = expandedSeries[seriesKey] ?? false;

                                                const isRenaming = renamingSeries?.key === seriesKey;
                                                // Show the pencil to all viewers; the backend rejects
                                                // non-owners with a 403 that surfaces as a toast, so
                                                // we don't need to gate on `dataset.is_owner` here.
                                                const canRename = true;

                                                return (
                                                    <div key={series.series_id} className="series-section" style={{ marginBottom: '0.25rem' }}>
                                                        <div
                                                            className="series-header"
                                                            onClick={isRenaming ? undefined : () => toggleSeries(patientId, study.study_id, series.series_id)}
                                                            style={{
                                                                cursor: isRenaming ? 'default' : 'pointer',
                                                                padding: '0.4rem 0.75rem',
                                                                backgroundColor: '#f8fafc',
                                                                borderRadius: '4px',
                                                                border: '1px solid #e2e8f0',
                                                                display: 'flex',
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            <i className={`fa ${isSeriesExpanded ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`} style={{ fontSize: '0.8rem' }}></i>
                                                            <i className={`fa ${series.file_type === 'dicom' ? 'fa-x-ray' : 'fa-cube'} me-2`} style={{ color: '#6366f1' }}></i>
                                                            {isRenaming ? (
                                                                <span style={{ flex: 1, display: 'flex', gap: '0.25rem', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control form-control-sm"
                                                                        style={{ flex: 1 }}
                                                                        autoFocus
                                                                        value={renamingSeries.draft}
                                                                        disabled={renameSubmitting}
                                                                        onChange={(e) => setRenamingSeries({ ...renamingSeries, draft: e.target.value })}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter') { e.preventDefault(); submitRenameSeries(); }
                                                                            else if (e.key === 'Escape') { e.preventDefault(); cancelRenameSeries(); }
                                                                        }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-success"
                                                                        disabled={renameSubmitting}
                                                                        onClick={(e) => { e.stopPropagation(); submitRenameSeries(); }}
                                                                        title="Save name"
                                                                    >
                                                                        <i className="fa fa-check"></i>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-secondary"
                                                                        disabled={renameSubmitting}
                                                                        onClick={(e) => { e.stopPropagation(); cancelRenameSeries(); }}
                                                                        title="Cancel"
                                                                    >
                                                                        <i className="fa fa-times"></i>
                                                                    </button>
                                                                </span>
                                                            ) : (
                                                                <>
                                                                    <span style={{ flex: 1 }}>{series.series_id}</span>
                                                                    {canRename && (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-secondary me-2"
                                                                            style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem' }}
                                                                            title="Rename series"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                startRenameSeries(patientId, study.study_id, series.series_id, series.series_id);
                                                                            }}
                                                                        >
                                                                            <i className="fa fa-pencil-square me-1"></i>Rename
                                                                        </button>
                                                                    )}
                                                                </>
                                                            )}
                                                            <span className="badge bg-dark me-1" style={{ fontSize: '0.7rem' }}>
                                                                {(series.file_type || 'unknown').toUpperCase()}
                                                            </span>
                                                            <span className="badge bg-info" style={{ fontSize: '0.7rem' }}>
                                                                {series.file_count} files
                                                            </span>
                                                        </div>

                                                        {isSeriesExpanded && series.files && (
                                                            <div className="files-list" style={{ paddingLeft: '1.5rem', paddingTop: '0.25rem' }}>
                                                                <div className="data-grid">
                                                                    {series.files.map(item => renderDataItem(item, 'imaging'))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const renderModalitySection = (patientId, modality, items) => {
        if (!items || items.length === 0) return null;
        if (activeTab !== "all" && activeTab !== modality) return null;

        const config = modalityConfig[modality];
        const isExpanded = expandedModalities[patientId]?.[modality] ?? true;

        return (
            <div key={modality} className={`modality-section modality-${modality}`}>
                <div
                    className="modality-header"
                    onClick={() => toggleModality(patientId, modality)}
                    style={{ cursor: 'pointer' }}
                >
                    <div className="modality-title">
                        <i className={`fa ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`}></i>
                        <i className={`fa ${config.icon} me-2`}></i>
                        <span className={`badge bg-${config.color} me-2`}>{config.label}</span>
                        <span className="modality-count">({items.length})</span>
                    </div>
                </div>
                {isExpanded && (
                    <div className="modality-content">
                        <div className="data-grid">
                            {items.map(item => renderDataItem(item, modality))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const filteredData = getFilteredData();

    return (
        <div className="datasets-page">
            <div className="starfield-background"></div>

            <Navbar />
            <ToastContainer autoClose={3000} />

            <div className="container py-4">
                {/* Back Button */}
                <button
                    className="back-button"
                    onClick={() => navigate("/Datasets")}
                >
                    <i className="fa fa-arrow-left"></i>
                    Back to Datasets
                </button>

                {/* Dataset Header Card */}
                <div className="datasets-header-card">
                    <div className="datasets-header-content">
                        <div className="datasets-info">
                            <div className="d-flex align-items-center gap-3 mb-2 flex-wrap">
                                <h1 className="datasets-title mb-0">{dataset?.name || "Dataset"}</h1>
                                <button
                                    className="btn btn-success"
                                    onClick={() => setShowAddPatientsModal(true)}
                                >
                                    <i className="fa fa-user-plus me-2"></i>
                                    Add Patients
                                </button>
                            </div>

                            {/* Dataset actions row (moved from /Datasets list) */}
                            {dataset && (
                                <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                                    {dataset.is_owner ? (
                                        <>
                                            <button
                                                className={`btn btn-sm ${dataset.is_public ? 'btn-success' : 'btn-outline-secondary'}`}
                                                onClick={handleTogglePublic}
                                                title={dataset.is_public ? "Click to make private" : "Click to make public"}
                                            >
                                                <i className={`fa ${dataset.is_public ? 'fa-globe' : 'fa-lock'} me-1`}></i>
                                                {dataset.is_public ? 'Public' : 'Private'}
                                            </button>
                                            {dataset.is_public && (
                                                <button
                                                    className={`btn btn-sm ${dataset.allow_download ? 'btn-info' : 'btn-outline-secondary'}`}
                                                    onClick={handleToggleDownload}
                                                    title={dataset.allow_download ? "Downloads enabled — click to disable" : "Downloads disabled — click to enable"}
                                                >
                                                    <i className={`fa ${dataset.allow_download ? 'fa-download' : 'fa-ban'} me-1`}></i>
                                                    {dataset.allow_download ? 'Download On' : 'Download Off'}
                                                </button>
                                            )}
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => setShowEditModal(true)}
                                                title="Edit Dataset"
                                            >
                                                <i className="fa fa-edit me-1"></i>
                                                Edit
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => setShowAssignModal(true)}
                                                title="Assign to Project"
                                            >
                                                <i className="fa fa-folder-open me-1"></i>
                                                Assign to Project
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => setDeleteModal({ show: true, deletePatients: false })}
                                                title="Delete Dataset"
                                            >
                                                <i className="fa fa-trash me-1"></i>
                                                Delete
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <span className={`badge ${dataset.is_available ? 'bg-info' : 'bg-secondary'}`}>
                                                <i className="fa fa-users me-1"></i>
                                                Shared with me
                                            </span>
                                            {dataset.is_added && dataset.is_available && (
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => setShowAssignModal(true)}
                                                    title="Assign to Project"
                                                >
                                                    <i className="fa fa-folder-open me-1"></i>
                                                    Assign to Project
                                                </button>
                                            )}
                                            {dataset.is_added && (
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={handleRemoveFromMyDatasets}
                                                    title="Remove from My Datasets"
                                                >
                                                    <i className="fa fa-times me-1"></i>
                                                    Remove from My Datasets
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            <p className="datasets-description">
                                {dataset?.description || "No description provided"}
                            </p>
                            {dataset?.project && (
                                <span className="project-badge mt-2">
                                    <i className="fa fa-folder-open me-1"></i>
                                    Project Assigned
                                </span>
                            )}
                        </div>
                        <div className="dataset-meta-info">
                            <div className="meta-stat">
                                <span className="meta-value">{totals.patients}</span>
                                <span className="meta-label">Patients</span>
                            </div>
                            <div className="meta-stat">
                                <span className="meta-value">{totals.imaging}</span>
                                <span className="meta-label">Imaging</span>
                            </div>
                            <div className="meta-stat">
                                <span className="meta-value">{totals.ehr}</span>
                                <span className="meta-label">EHR</span>
                            </div>
                            <div className="meta-stat">
                                <span className="meta-value">{totals.annotations}</span>
                                <span className="meta-label">Annotations</span>
                            </div>
                            <div className="meta-stat">
                                <span className="meta-value">{totals.maps}</span>
                                <span className="meta-label">Maps</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CLI Pull Command Card */}
                {dataset && (dataset.is_owner || (dataset.is_public && dataset.allow_download)) && (
                    <div className="datasets-card mb-4">
                        <div className="datasets-card-content" style={{ padding: "1rem" }}>
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                    <i className="fa fa-terminal me-3" style={{ fontSize: '1.25rem', color: '#6c757d' }}></i>
                                    <div>
                                        <small className="text-muted d-block mb-1">Pull this dataset</small>
                                        <code
                                            className="d-block px-3 py-2"
                                            style={{
                                                backgroundColor: '#1a1a2e',
                                                color: '#00ff88',
                                                borderRadius: '4px',
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            dv datasets pull {dataset.created_by_user?.username || dataset.created_by_username}/{dataset.name}
                                        </code>
                                    </div>
                                </div>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => {
                                        navigator.clipboard.writeText(
                                            `dv datasets pull ${dataset.created_by_user?.username || dataset.created_by_username}/${dataset.name}`
                                        );
                                        toast.success("Command copied to clipboard!");
                                    }}
                                    title="Copy to clipboard"
                                >
                                    <i className="fa fa-copy"></i>
                                </button>
                            </div>
                            {!dataset.is_owner && dataset.is_public && (
                                <small className="text-muted mt-2 d-block">
                                    <i className="fa fa-info-circle me-1"></i>
                                    This public dataset allows downloads
                                </small>
                            )}
                        </div>
                    </div>
                )}

                {/* Filter Tabs */}
                <div className="datasets-card mb-4">
                    <div className="datasets-card-content" style={{ padding: "1rem" }}>
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div className="filter-tabs">
                                <button
                                    className={`filter-tab ${activeTab === "all" ? "active" : ""}`}
                                    onClick={() => setActiveTab("all")}
                                >
                                    <i className="fa fa-th-large me-2"></i>
                                    All ({totals.total})
                                </button>
                                <button
                                    className={`filter-tab ${activeTab === "imaging" ? "active" : ""}`}
                                    onClick={() => setActiveTab("imaging")}
                                >
                                    <i className="fa fa-x-ray me-2"></i>
                                    Imaging ({totals.imaging})
                                </button>
                                <button
                                    className={`filter-tab ${activeTab === "ehr" ? "active" : ""}`}
                                    onClick={() => setActiveTab("ehr")}
                                >
                                    <i className="fa fa-heartbeat me-2"></i>
                                    EHR ({totals.ehr})
                                </button>
                                <button
                                    className={`filter-tab ${activeTab === "annotations" ? "active" : ""}`}
                                    onClick={() => setActiveTab("annotations")}
                                >
                                    <i className="fa fa-pencil-square me-2"></i>
                                    Annotations ({totals.annotations})
                                </button>
                                <button
                                    className={`filter-tab ${activeTab === "maps" ? "active" : ""}`}
                                    onClick={() => setActiveTab("maps")}
                                >
                                    <i className="fa fa-map me-2"></i>
                                    Maps ({totals.maps})
                                </button>
                                <button
                                    className={`filter-tab ${activeTab === "documents" ? "active" : ""}`}
                                    onClick={() => setActiveTab("documents")}
                                >
                                    <i className="fa fa-file-text me-2"></i>
                                    Documents ({totals.documents})
                                </button>
                            </div>
                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={expandAllPatients}
                                    title="Expand All"
                                >
                                    <i className="fa fa-expand me-1"></i> Expand All
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={collapseAllPatients}
                                    title="Collapse All"
                                >
                                    <i className="fa fa-compress me-1"></i> Collapse All
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Data by Patient */}
                {loading ? (
                    <div className="datasets-card">
                        <div className="datasets-card-content">
                            <div className="loading-state">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : Object.keys(filteredData).length === 0 ? (
                    <div className="datasets-card">
                        <div className="datasets-card-content">
                            <div className="empty-state">
                                <i className="fa fa-database"></i>
                                <p>No data in this dataset</p>
                                <small className="text-muted">Add data to this dataset to view them here</small>
                            </div>
                        </div>
                    </div>
                ) : (
                    Object.entries(filteredData).map(([patientId, patientData]) => {
                        const counts = patientData.counts || {};
                        const isExpanded = expandedPatients[patientId];

                        return (
                            <div key={patientId} className="datasets-card mb-3 patient-card">
                                <div
                                    className="datasets-card-header patient-header"
                                    onClick={() => togglePatient(patientId)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className="datasets-card-title">
                                        <i className={`fa ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'} datasets-card-icon`}></i>
                                        <div>
                                            <h3>
                                                <i className="fa fa-user me-2"></i>
                                                Patient: {patientId}
                                            </h3>
                                            <div className="patient-badges">
                                                {counts.imaging > 0 && (
                                                    <span className="badge bg-primary me-1">
                                                        <i className="fa fa-x-ray me-1"></i>{counts.imaging} Imaging
                                                        {counts.studies > 0 && ` (${counts.studies} studies, ${counts.series || 0} series)`}
                                                    </span>
                                                )}
                                                {counts.ehr > 0 && (
                                                    <span className="badge bg-danger me-1">
                                                        <i className="fa fa-heartbeat me-1"></i>{counts.ehr} EHR
                                                    </span>
                                                )}
                                                {counts.annotations > 0 && (
                                                    <span className="badge bg-purple me-1">
                                                        <i className="fa fa-pencil-square me-1"></i>{counts.annotations} Annotations
                                                    </span>
                                                )}
                                                {counts.maps > 0 && (
                                                    <span className="badge bg-teal me-1">
                                                        <i className="fa fa-map me-1"></i>{counts.maps} Maps
                                                    </span>
                                                )}
                                                {counts.documents > 0 && (
                                                    <span className="badge bg-warning me-1">
                                                        <i className="fa fa-file-text me-1"></i>{counts.documents} docs
                                                    </span>
                                                )}
                                                {counts.signals > 0 && (
                                                    <span className="badge bg-secondary me-1">
                                                        <i className="fa fa-wave-square me-1"></i>{counts.signals} signals
                                                    </span>
                                                )}
                                                {counts.tables > 0 && (
                                                    <span className="badge bg-info me-1">
                                                        <i className="fa fa-table me-1"></i>{counts.tables} tables
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="datasets-card-content patient-content">
                                        {/* Render modalities - use hierarchy for imaging */}
                                        {renderImagingHierarchy(patientId, patientData)}
                                        {renderModalitySection(patientId, 'ehr', patientData.modalities?.ehr)}
                                        {renderModalitySection(patientId, 'documents', patientData.modalities?.documents)}
                                        {renderModalitySection(patientId, 'signals', patientData.modalities?.signals)}
                                        {renderModalitySection(patientId, 'tables', patientData.modalities?.tables)}
                                        {renderModalitySection(patientId, 'images', patientData.modalities?.images)}

                                        {/* Annotations section - separate with special styling */}
                                        {patientData.annotations && patientData.annotations.length > 0 && (activeTab === "all" || activeTab === "annotations") && (
                                            <div className="annotations-section">
                                                <div
                                                    className="modality-header annotations-header"
                                                    onClick={() => toggleModality(patientId, 'annotations')}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className="modality-title">
                                                        <i className={`fa ${expandedModalities[patientId]?.annotations ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`}></i>
                                                        <i className="fa fa-pencil-square me-2"></i>
                                                        <span className="badge bg-purple me-2">Annotations / Segmentations</span>
                                                        <span className="modality-count">({patientData.annotations.length})</span>
                                                        <small className="text-muted ms-2">
                                                            <i className="fa fa-info-circle me-1"></i>
                                                            Can be included/excluded when adding to study
                                                        </small>
                                                    </div>
                                                </div>
                                                {expandedModalities[patientId]?.annotations && (
                                                    <div className="modality-content">
                                                        <div className="data-grid">
                                                            {patientData.annotations.map(item => renderDataItem(item, 'annotations'))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Maps section - separate with special styling */}
                                        {patientData.maps && patientData.maps.length > 0 && (activeTab === "all" || activeTab === "maps") && (
                                            <div className="maps-section">
                                                <div
                                                    className="modality-header maps-header"
                                                    onClick={() => toggleModality(patientId, 'maps')}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className="modality-title">
                                                        <i className={`fa ${expandedModalities[patientId]?.maps ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`}></i>
                                                        <i className="fa fa-map me-2"></i>
                                                        <span className="badge bg-teal me-2">Maps / Probability Maps</span>
                                                        <span className="modality-count">({patientData.maps.length})</span>
                                                        <small className="text-muted ms-2">
                                                            <i className="fa fa-info-circle me-1"></i>
                                                            Can be loaded in viewer as overlay
                                                        </small>
                                                    </div>
                                                </div>
                                                {expandedModalities[patientId]?.maps && (
                                                    <div className="modality-content">
                                                        <div className="data-grid">
                                                            {patientData.maps.map(item => renderDataItem(item, 'maps'))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Legacy: Show any MedicalImage records */}
                                        {patientData.images && patientData.images.length > 0 && activeTab === "all" && (
                                            <div className="modality-section modality-legacy">
                                                <div className="modality-header">
                                                    <div className="modality-title">
                                                        <i className="fa fa-image me-2"></i>
                                                        <span className="badge bg-info me-2">Legacy Images</span>
                                                        <span className="modality-count">({patientData.images.length})</span>
                                                    </div>
                                                </div>
                                                <div className="modality-content">
                                                    <div className="data-grid">
                                                        {patientData.images.map((image) => (
                                                            <div key={image.id} className="data-item">
                                                                <div className="data-item-icon">
                                                                    <i className="fa fa-file-image-o"></i>
                                                                </div>
                                                                <div className="data-item-info">
                                                                    <span className="data-item-name">{image.series_name || 'Image'}</span>
                                                                    <span className="data-item-meta">
                                                                        {image.modality && <span className="badge bg-info me-1">{image.modality}</span>}
                                                                        {image.description && <small>{image.description}</small>}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Add Patients Modal */}
            {showAddPatientsModal && (
                <div className="modal-overlay" onClick={() => setShowAddPatientsModal(false)}>
                    <div className="modal-content add-patients-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                <i className="fa fa-user-plus me-2"></i>
                                Add Patients to Dataset
                            </h3>
                            <button
                                className="modal-close"
                                onClick={() => setShowAddPatientsModal(false)}
                            >
                                <i className="fa fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <PatientSelector
                                selectedPatients={selectedPatients}
                                onPatientsChange={setSelectedPatients}
                            />
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn btn-secondary"
                                onClick={() => {
                                    setShowAddPatientsModal(false);
                                    setSelectedPatients([]);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-success"
                                onClick={handleAddPatients}
                                disabled={addingPatients || selectedPatients.length === 0}
                            >
                                {addingPatients ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Adding...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-plus me-2"></i>
                                        Add {selectedPatients.length} Patient{selectedPatients.length !== 1 ? 's' : ''}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* EHR Viewer Modal */}
            {showEHRViewer && selectedEHRData && (
                <EHRViewer
                    ehrData={selectedEHRData}
                    onClose={() => {
                        setShowEHRViewer(false);
                        setSelectedEHRData(null);
                    }}
                />
            )}

            {/* Edit Dataset Modal */}
            {showEditModal && dataset && (
                <DatasetFormModal
                    dataset={dataset}
                    projects={projects}
                    onClose={() => setShowEditModal(false)}
                    onSuccess={() => {
                        setShowEditModal(false);
                        fetchDataset();
                    }}
                />
            )}

            {/* Assign to Project Modal */}
            {showAssignModal && dataset && (
                <AssignProjectModal
                    dataset={dataset}
                    projects={projects}
                    onClose={() => setShowAssignModal(false)}
                    onSuccess={() => {
                        setShowAssignModal(false);
                        fetchDataset();
                    }}
                />
            )}

            {/* Delete Dataset Confirmation Modal */}
            {deleteModal.show && dataset && (
                <div className="modal-overlay" onClick={() => setDeleteModal({ show: false, deletePatients: false })}>
                    <div className="modal-content delete-dataset-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                <i className="fa fa-exclamation-triangle text-danger me-2"></i>
                                Delete Dataset
                            </h3>
                            <button
                                className="modal-close-btn"
                                onClick={() => setDeleteModal({ show: false, deletePatients: false })}
                            >
                                <i className="fa fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>
                                Are you sure you want to delete the dataset <strong>"{dataset.name}"</strong>?
                            </p>
                            <p className="text-muted mb-3">
                                This dataset contains <strong>{dataset.patient_count || 0}</strong> patient(s).
                            </p>

                            {(dataset.patient_count > 0) && (
                                <div className="delete-patients-option">
                                    <label className="delete-option-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={deleteModal.deletePatients}
                                            onChange={(e) => setDeleteModal(prev => ({ ...prev, deletePatients: e.target.checked }))}
                                        />
                                        <span className="checkmark"></span>
                                        <span className="option-text">
                                            Also delete all {dataset.patient_count} patient(s) in this dataset
                                        </span>
                                    </label>
                                    {deleteModal.deletePatients && (
                                        <div className="delete-warning">
                                            <i className="fa fa-exclamation-circle me-2"></i>
                                            <strong>Warning:</strong> This will permanently delete all patients and their associated data (images, documents, etc.). This action cannot be undone.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setDeleteModal({ show: false, deletePatients: false })}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-danger"
                                onClick={confirmDeleteDataset}
                            >
                                <i className="fa fa-trash me-2"></i>
                                {deleteModal.deletePatients ? 'Delete Dataset & Patients' : 'Delete Dataset'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DatasetList;
